import React, { useState } from 'react';
import classNames from 'classnames';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import './ImportSourceList.scss';
import { NSCard } from 'bricks';
import { IOptionAnyValue } from 'bricks/types';
import { OrganizationSubproject } from 'views/ProFormaTable/types';
import RenderIf from 'components/RenderIf/RenderIf';
import { DealFromSignup } from 'views/ProFormaTable/hooks/useGetDealsWithProFormas';
import ProjectSourceItem from './ProjectSourceItem/ProjectSourceItem';
import DealSourceItem from './DealSourceItem/DealSourceItem';

interface IImportSourceListProps {
    organizationSubprojects?: OrganizationSubproject[];
    deals?: DealFromSignup[];
    selectedProjectId?: number;
    onProjectSelect?: (projectId: number) => void;
    onDealSelect?: (dealId: number) => void;
    onProFormaSelect?: (proFormaId: number) => void;
    budgetEscalation?: string;
    handleBudgetEscalationChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    searchValue?: string;
    isProjectsLoading?: boolean;
    isDealsLoading?: boolean;
    view?: 'projects' | 'deals';
}

const ImportSourceList = ({
    organizationSubprojects = [],
    deals = [],
    selectedProjectId,
    onProjectSelect = () => {},
    onDealSelect = () => {},
    onProFormaSelect = () => {},
    budgetEscalation = '',
    handleBudgetEscalationChange = () => {},
    searchValue = '',
    isProjectsLoading = false,
    isDealsLoading = false,
    view = 'projects',
}: IImportSourceListProps) => {
    const [checkboxStates, setCheckboxStates] = useState<Record<string, boolean>>({});
    const [selectedDealId, setSelectedDealId] = useState<number | null>(null);
    const [selectedProFormaId, setSelectedProFormaId] = useState<number | null>(null);
    const [selectedScenarios, setSelectedScenarios] = useState<Record<number, IOptionAnyValue | undefined>>({});

    const isLoading = view === 'projects' ? isProjectsLoading : isDealsLoading;
    const hasNoContent = view === 'deals' ? !deals.length : !organizationSubprojects.length;

    const handleCheckboxChange = (id: string) => {
        if (id.startsWith('budget-cat-')) {
            return;
        }
        setCheckboxStates(prev => ({
            ...prev,
            [id]: !(id in prev ? prev[id] : true),
        }));
    };

    const handleDealSelect = (dealId: number) => {
        if (dealId !== selectedDealId) {
            setSelectedDealId(dealId);
            setSelectedProFormaId(null);
            onDealSelect(dealId);
        }
    };

    const handleProFormaSelect = (proFormaId: number) => {
        setSelectedProFormaId(proFormaId);
        onProFormaSelect(proFormaId);
    };

    const handleScenarioSelect = (projectId: number) => (option: IOptionAnyValue | undefined) => {
        setSelectedScenarios(prev => ({
            ...prev,
            [projectId]: option,
        }));
    };

    const EmptyState = () => (
        <div className="d-flex flex-column align-items-center justify-content-center p-4">
            <div className="text-muted py-5">
                {!searchValue
                    ? `You currently have no ${view === 'deals' ? 'deals' : 'projects'} available to populate this section.`
                    : (
                        <>
                            No
                            {' '}
                            {view === 'deals' ? 'deal' : 'project'}
                            {' '}
                            results for
                            <span className="text-white font-weight-bold pl-1">
                                "
                                {searchValue}
                                "
                            </span>
                        </>
                    )}
            </div>
        </div>
    );

    return (
        <div className="project-list">
            <RenderIf isLoading={isLoading} isTrue>
                <RenderIf isTrue={!isLoading && hasNoContent}>
                    <EmptyState />
                </RenderIf>
                <RenderIf isTrue={!isLoading && !hasNoContent}>
                    <OverlayScrollbarsComponent
                        defer
                        options={{
                            scrollbars: { autoHide: 'leave' },
                            overflow: { y: 'scroll', x: 'hidden' },
                        }}
                        className="OverlayScrollbarsComponent overflow-auto"
                        style={{
                            maxHeight: '500px',
                        }}
                    >
                        <RenderIf isTrue={view === 'projects'}>
                            {organizationSubprojects.map(({ subproject }, index) => (
                                <NSCard
                                    key={subproject.id}
                                    className={classNames('mb-0', {
                                        'mb-1': subproject.id === selectedProjectId,
                                    })}
                                >
                                    <ProjectSourceItem
                                        project={subproject}
                                        isLast={index === organizationSubprojects.length - 1}
                                        selectedProjectId={selectedProjectId}
                                        onProjectSelect={onProjectSelect}
                                        checkboxStates={checkboxStates}
                                        handleCheckboxChange={handleCheckboxChange}
                                        selectedScenarios={selectedScenarios}
                                        handleScenarioSelect={handleScenarioSelect}
                                        budgetEscalation={budgetEscalation}
                                        handleBudgetEscalationChange={handleBudgetEscalationChange}
                                    />
                                </NSCard>
                            ))}
                        </RenderIf>
                        <RenderIf isTrue={view === 'deals'}>
                            {deals.map((deal, index) => (
                                <NSCard
                                    key={deal.id}
                                    className={classNames('mb-0', {
                                        'mb-1': deal.id === selectedDealId,
                                    })}
                                >
                                    <DealSourceItem
                                        deal={deal}
                                        isLast={index === deals.length - 1}
                                        selectedDealId={selectedDealId}
                                        handleDealSelect={handleDealSelect}
                                        selectedProFormaId={selectedProFormaId}
                                        handleProFormaSelect={handleProFormaSelect}
                                    />
                                </NSCard>
                            ))}
                        </RenderIf>
                    </OverlayScrollbarsComponent>
                </RenderIf>
            </RenderIf>
        </div>
    );
};

export default ImportSourceList;
